/**
 * API Client
 * Handles all API requests to the backend
 */

import { fetchAuthSession } from 'aws-amplify/auth'

const API_BASE_URL = 'https://35p69bdfu0.execute-api.us-east-1.amazonaws.com/production/api'

/**
 * Get authentication token from Cognito session
 */
async function getAuthToken() {
  try {
    const session = await fetchAuthSession({ forceRefresh: false })
    const token = session.tokens?.idToken?.toString()

    if (!token) {
      console.error('No token found in session:', session)
    } else {
      console.log('Token retrieved successfully, length:', token.length)
    }

    return token
  } catch (error) {
    console.error('Error getting auth token:', error)
    return null
  }
}

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const token = await getAuthToken()

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  }

  // Only add Authorization header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
    console.log('Making API request to:', endpoint, 'with auth token')
  } else {
    console.log('Making API request to:', endpoint, 'WITHOUT auth token')
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers
  })

  const data = await response.json()

  if (!response.ok) {
    console.error('API request failed:', {
      endpoint,
      status: response.status,
      statusText: response.statusText,
      error: data.error,
      hasToken: !!token
    })
    throw new Error(data.error?.message || 'API request failed')
  }

  console.log('API request successful:', endpoint)
  return data
}

// ========================================
// Clients API
// ========================================

export const clientsAPI = {
  /**
   * Get all clients
   */
  async getAll(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const endpoint = `/clients${queryString ? `?${queryString}` : ''}`
    return apiRequest(endpoint)
  },

  /**
   * Get single client by ID
   */
  async getById(clientId) {
    return apiRequest(`/clients/${clientId}`)
  },

  /**
   * Create new client
   */
  async create(clientData) {
    return apiRequest('/clients', {
      method: 'POST',
      body: JSON.stringify(clientData)
    })
  },

  /**
   * Update existing client
   */
  async update(clientId, clientData) {
    return apiRequest(`/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(clientData)
    })
  },

  /**
   * Delete client
   */
  async delete(clientId) {
    return apiRequest(`/clients/${clientId}`, {
      method: 'DELETE'
    })
  }
}

// ========================================
// Cards API
// ========================================

export const cardsAPI = {
  /**
   * Get all cards
   */
  async getAll(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const endpoint = `/cards${queryString ? `?${queryString}` : ''}`
    return apiRequest(endpoint)
  },

  /**
   * Get cards by client ID
   */
  async getByClientId(clientId) {
    return apiRequest(`/cards?client_id=${clientId}`)
  },

  /**
   * Get single card by ID
   */
  async getById(cardId) {
    return apiRequest(`/cards/${cardId}`)
  },

  /**
   * Create new card
   */
  async create(cardData) {
    return apiRequest('/cards', {
      method: 'POST',
      body: JSON.stringify(cardData)
    })
  },

  /**
   * Update existing card
   */
  async update(cardId, cardData) {
    return apiRequest(`/cards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify(cardData)
    })
  },

  /**
   * Delete card
   */
  async delete(cardId) {
    return apiRequest(`/cards/${cardId}`, {
      method: 'DELETE'
    })
  },

  /**
   * Activate card
   */
  async activate(cardId) {
    return apiRequest(`/cards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'active' })
    })
  },

  /**
   * Deactivate card
   */
  async deactivate(cardId) {
    return apiRequest(`/cards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'inactive' })
    })
  },

  /**
   * Assign card to client
   */
  async assign(cardId, assignmentData) {
    return apiRequest(`/cards/${cardId}/assign`, {
      method: 'POST',
      body: JSON.stringify(assignmentData)
    })
  },

  /**
   * Unassign card from client
   */
  async unassign(cardId) {
    return apiRequest(`/cards/${cardId}/unassign`, {
      method: 'POST'
    })
  }
}

// ========================================
// Events API
// ========================================

export const eventsAPI = {
  /**
   * Get all events
   */
  async getAll(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const endpoint = `/events${queryString ? `?${queryString}` : ''}`
    return apiRequest(endpoint)
  },

  /**
   * Get events by device
   */
  async getByDevice(deviceId, params = {}) {
    const queryString = new URLSearchParams({ ...params, device_id: deviceId }).toString()
    return apiRequest(`/events?${queryString}`)
  },

  /**
   * Delete events by date range
   */
  async deleteByDateRange(from, to) {
    return apiRequest(`/events?from=${from}&to=${to}`, {
      method: 'DELETE'
    })
  }
}

// ========================================
// Devices API
// ========================================

export const devicesAPI = {
  /**
   * Get all devices
   */
  async getAll(params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const endpoint = `/devices${queryString ? `?${queryString}` : ''}`
    return apiRequest(endpoint)
  },

  /**
   * Get single device by ID
   */
  async getById(deviceId) {
    return apiRequest(`/devices/${deviceId}`)
  },

  /**
   * Create new device
   */
  async create(deviceData) {
    return apiRequest('/devices', {
      method: 'POST',
      body: JSON.stringify(deviceData)
    })
  },

  /**
   * Update existing device
   */
  async update(deviceId, deviceData) {
    return apiRequest(`/devices/${deviceId}`, {
      method: 'PUT',
      body: JSON.stringify(deviceData)
    })
  },

  /**
   * Delete device
   */
  async delete(deviceId) {
    return apiRequest(`/devices/${deviceId}`, {
      method: 'DELETE'
    })
  }
}

// ========================================
// Users API
// ========================================

export const usersAPI = {
  /**
   * Get all users
   */
  async getAll() {
    return apiRequest('/users')
  },

  /**
   * Get single user by ID
   */
  async getById(userId) {
    return apiRequest(`/users/${userId}`)
  },

  /**
   * Create new user
   */
  async create(userData) {
    return apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    })
  },

  /**
   * Update user
   */
  async update(userId, userData) {
    return apiRequest(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
  },

  /**
   * Delete user
   */
  async delete(userId) {
    return apiRequest(`/users/${userId}`, {
      method: 'DELETE'
    })
  },

  /**
   * Reset user password (admin only)
   */
  async resetPassword(userId) {
    return apiRequest(`/users/${userId}/reset-password`, {
      method: 'POST'
    })
  }
}

// ========================================
// Roles API
// ========================================

export const rolesAPI = {
  /**
   * Get all roles
   */
  async getAll() {
    return apiRequest('/roles')
  },

  /**
   * Get single role by ID
   */
  async getById(roleId) {
    return apiRequest(`/roles/${roleId}`)
  },

  /**
   * Create new role
   */
  async create(roleData) {
    return apiRequest('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    })
  },

  /**
   * Update role
   */
  async update(roleId, roleData) {
    return apiRequest(`/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    })
  },

  /**
   * Delete role
   */
  async delete(roleId) {
    return apiRequest(`/roles/${roleId}`, {
      method: 'DELETE'
    })
  }
}

// ========================================
// Organizations API
// ========================================

export const organizationsAPI = {
  /**
   * Get all organizations (super admin only)
   */
  async getAll() {
    return apiRequest('/organizations')
  },

  /**
   * Get user's organization assignments
   */
  async getUserOrganizations(userId) {
    const endpoint = userId
      ? `/user_organizations?user_id=${userId}`
      : '/user_organizations'
    return apiRequest(endpoint)
  },

  /**
   * Get users in an organization
   */
  async getOrganizationUsers(orgId) {
    return apiRequest(`/user_organizations/org/${orgId}/users`)
  },

  /**
   * Get single organization by ID
   */
  async getById(orgId) {
    return apiRequest(`/organizations/${orgId}`)
  },

  /**
   * Create new organization (super admin only)
   */
  async create(orgData) {
    return apiRequest('/organizations', {
      method: 'POST',
      body: JSON.stringify(orgData)
    })
  },

  /**
   * Update organization (super admin only)
   */
  async update(orgId, orgData) {
    return apiRequest(`/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(orgData)
    })
  },

  /**
   * Delete organization (super admin only)
   */
  async delete(orgId) {
    return apiRequest(`/organizations/${orgId}`, {
      method: 'DELETE'
    })
  },

  /**
   * Assign user to organization (super admin only)
   */
  async assignUser(userId, orgId, roleId) {
    return apiRequest('/user_organizations', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, org_id: orgId, role_id: roleId })
    })
  },

  /**
   * Remove user from organization (super admin only)
   */
  async removeUser(userId, orgId) {
    return apiRequest(`/user_organizations/${userId}/${orgId}`, {
      method: 'DELETE'
    })
  }
}
